import { SUCCESS_FEE_MIGRATION_QUERY_KEY } from '@/hooks/queries/use-success-fee-migration';
import { SUCCESS_FEE_TERMS_QUERY_KEY } from '@/hooks/queries/use-success-fee-terms';
import { useQueryClient } from '@tanstack/react-query';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';

/** Value Shopify sends back on the return URL after the approval page. */
export const MIGRATION_RETURNED = 'returned';

/**
 * Handle the landing on `/settings/billing?migration=returned`.
 *
 * The approval only reaches us through the Shopify webhook, so the status the
 * page held before leaving is already stale. Refetch once, keep the card on a
 * neutral placeholder until it lands so the stale state never flashes, then drop
 * the parameter so a manual refresh does not replay the flash.
 *
 * @returns whether the post-return refetch is still in flight
 */
export function useSuccessFeeMigrationReturn(): boolean {
  const [migration, setMigration] = useQueryState('migration', parseAsString);
  const queryClient = useQueryClient();
  const [isReturning, setIsReturning] = useState(false);

  useEffect(() => {
    if (migration !== MIGRATION_RETURNED) return;

    let cancelled = false;
    setIsReturning(true);

    // The terms query is refetched beside the migration one because the notice
    // across the top of the page is decided from its deadline: leaving it stale
    // would keep telling a merchant who has just approved that a step remains.
    void Promise.all([
      queryClient.refetchQueries({ queryKey: SUCCESS_FEE_MIGRATION_QUERY_KEY }),
      queryClient.refetchQueries({ queryKey: SUCCESS_FEE_TERMS_QUERY_KEY }),
    ]).finally(() => {
      if (cancelled) return;
      setIsReturning(false);
      void setMigration(null, { history: 'replace' });
    });

    return () => {
      cancelled = true;
    };
  }, [migration, queryClient, setMigration]);

  return isReturning;
}
